import * as zExt from "@redotech/rpc/ext";
import { Percentage } from "@redotech/util/percentage";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { SchemaType } from "./advanced-flow/schemas/schemas";
import { categories } from "./advanced-flow/triggers";
import { SocialPlatform } from "./brand-kit";
import {
  Alignment,
  buttonHierarchies,
  fontWeights,
} from "./content-builder/element-schema";
import { cropConfigV2Schema } from "./crop-config-v2";
import {
  ButtonLinkType,
  CartLayoutType,
  EmailBlockType,
  EmailBuilderFontWeight,
  EmailHeaderType,
  ObjectFit,
  Orientation,
  ProductSelectionType,
  QrCodeLinkType,
  ScratcherSize,
  ShoppableProductsSize,
  Size,
  SocialIconColor,
  SocialItemSource,
  TemplateType,
  VerticalAlignment,
} from "./email-builder";
import {
  delayOptionSchema,
  SubscriptionDateFormat,
  SubscriptionSkipOrDelayState,
} from "./email-builder/subscription-utils";
import { ImageType } from "./image-type";
import { Provider } from "./order";

export const defaultCreateGiftReceiptButton: BrandKitEmailButton = {
  override: {
    enabled: false,
    textColor: "#FFFFFF",
    border: { cornerRadiusPx: 0, stroke: { color: "#000000", weightPx: 0 } },
    paddingPx: 0,
    font: { fontFamily: "Arial", fontSizePx: 16, fontWeight: "regular" },
  },
  fullWidth: false,
  text: "Get Gift Receipt",
  hierarchy: "secondary",
};
export interface EmailTemplateRow extends EmailTemplate {
  _id: any;
}
export const paddingSchema = z.object({
  top: z.number().min(0),
  right: z.number().min(0),
  bottom: z.number().min(0),
  left: z.number().min(0),
});

const baseSectionSchema = z.object({
  blockId: zExt.objectId(),
  sectionPadding: paddingSchema,
  sectionColor: z.string(),
});

export const discountSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.DISCOUNT),
  discountId: zExt.objectId().optional(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  fontFamily: z.string(),
  fontWeight: createEnum(EmailBuilderFontWeight, {
    enumName: "EmailBuilderFontWeight",
  }),
  fontSize: z.number(),
  textColor: z.string(),
  blockBackgroundColor: z.string(),
});

export const columnSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.COLUMN),
  columns: z.array(z.lazy(() => nonRecursiveEmailBlocks.nullable())),
  columnCount: z.number(),
  gap: z.number(),
  stackOnMobile: z.boolean(),
  alignment: createEnum(VerticalAlignment, { enumName: "VerticalAlignment" }),
  columnWidths: z
    .array(
      z
        .number()
        .min(0)
        .max(100)
        .transform((n) => n as Percentage),
    )
    .nullish(), // note: there may be lingering bugs with this - the frontend had it long before the Zod schema did, due to an oversight
});

export const menuItemSchema = z.object({ id: z.string(), label: z.string() });

export const menuSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.MENU),
  menuItems: z.array(menuItemSchema),
  linkColor: z.string(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  fontFamily: z.string(),
  fontSize: z.number(),
  textColor: z.string(),
  stackOnMobile: z.boolean(),
  itemSpacing: z.number().optional(),
  useCustomSpacing: z.boolean().optional(),
});

export const qrCodeSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.QR_CODE),
  url: z.string().optional(),
  linkType: createEnum(QrCodeLinkType, { enumName: "QrCodeLinkType" }),
  schemaFieldName: z.string().optional(),
});

export const socialItemSchema = z.object({
  id: z.string(),
  platform: createEnum(SocialPlatform, { enumName: "SocialPlatform" }),
  url: z.string(),
  hidden: z.boolean().optional(),
  source: createEnum(SocialItemSource, {
    enumName: "SocialItemSource",
  }).optional(),
});

export const socialsSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SOCIALS),
  socialLinks: z.array(socialItemSchema),
  iconColor: createEnum(SocialIconColor, { enumName: "SocialIconColor" }),
  iconPadding: z.number(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  useBrandKitSocials: z.boolean().optional(),
});

export const headerSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.HEADER),
  headerType: createEnum(EmailHeaderType, { enumName: "EmailHeaderType" }),
  layout: createEnum(Alignment, { enumName: "Alignment" }),
  imageUrl: z.string(),
  text: z.string(),
  textColor: z.string(),
  fontSize: z.number(),
  fontFamily: z.string(),
  logoHeight: z.number(),
  imageHeight: z.number(),
  altText: z.string().optional(),
  clickthroughUrl: z.string().optional(),
});

export const footerSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.FOOTER),
  horizontalPadding: createEnum(Size, { enumName: "Size" }),
  verticalPadding: createEnum(Size, { enumName: "Size" }),
  padding: paddingSchema,
  textColor: z.string(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  fontSize: z.number().optional(),
  fontFamily: z.string().optional(),
  schemaFieldName: z.string().optional(),
  useTemplateAddress: z.boolean().optional(),
});

export const textSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.TEXT),
  textColor: z.string(),
  fontSize: z.number(),
  fontFamily: z.string(),
  linkColor: z.string(),
  text: z.string(),
});

export const buttonSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.BUTTON),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  cornerRadius: z.number(),
  buttonText: z.string(),
  padding: paddingSchema,
  buttonLink: z.string().optional(),
  fillColor: z.string(),
  strokeColor: z.string(),
  textColor: z.string(),
  strokeWeight: z.number(),
  fontFamily: z.string(),
  fontSize: z.number(),
  as: createEnum(["button", "a"], { enumName: "ButtonAs" }).optional(),
  buttonType: createEnum(["button", "submit", "reset"], {
    enumName: "ButtonType",
  }).optional(),
  fullWidth: z.boolean().optional(),
  linkType: createEnum(ButtonLinkType, { enumName: "ButtonLinkType" }),
  schemaFieldName: z.string().optional(),
});

export const inlineButtonSchema = buttonSchema.omit({
  blockId: true,
  sectionPadding: true,
  type: true,
  sectionColor: true,
  linkType: true,
});

const reviewRequestSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.REVIEW_REQUEST),
  sectionHeader: z.string(),
  imageCornerRadius: z.number(),
  imageSize: createEnum(Size, { enumName: "Size" }),
  buttonColor: z.string(),
  buttonTextColor: z.string(),
  buttonCornerRadius: z.number(),
  buttonPadding: paddingSchema,
  textColor: z.string(),
  fontFamily: z.string(),
  imageAspectRatio: z.number().nullish(),
  imageObjectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
  schemaFieldName: z.string().optional(),
});

export const lineSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.LINE),
  color: z.string(),
  padding: paddingSchema,
  horizontalPadding: createEnum(Size, { enumName: "Size" }),
  verticalPadding: createEnum(Size, { enumName: "Size" }),
});

export const spacerSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SPACER),
  height: z.number(),
});

// Define the pixel crop schema
const pixelCropSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  unit: z.literal("px"),
});

// Define the crop config schema
const cropConfigV1Schema = z.object({
  crop: pixelCropSchema,
  imageWidth: z.number(), // even if the true image source is larger, it will be scaled down to this width
  imageHeight: z.number(), // even if the true image source is larger, it will be scaled down to this height
  circularCrop: z.boolean(),
  // Ideally we would not store these width ratios, they are incidental complexity caused by the frontend.
  // We should probably get rid of them at some point by dividing imageWidth and imageHeight by them, and then setting these to 1.
  cropWidthRatio: z.number(),
  cropHeightRatio: z.number(),
});

/**
 * @deprecated
 * If you're reading this after July 2025, there is no reason to keep this interface around.
 */
export type CropConfigV1 = z.infer<typeof cropConfigV1Schema>;

export type CropConfigV2 = z.infer<typeof cropConfigV2Schema>;

/**
 * Converts a V1 crop config to V2 format
 * @param v1 The V1 crop config to convert
 * @returns V2 crop config with ratio-based coordinates
 */
export function convertCropConfigV1ToV2(v1: CropConfigV1): CropConfigV2 {
  // Apply the crop ratios to convert from displayed coordinates to natural image coordinates
  const adjustedCropX = v1.crop.x / v1.cropWidthRatio;
  const adjustedCropY = v1.crop.y / v1.cropHeightRatio;
  const adjustedCropWidth = v1.crop.width / v1.cropWidthRatio;
  const adjustedCropHeight = v1.crop.height / v1.cropHeightRatio;

  const widthRatio = adjustedCropWidth / v1.imageWidth;
  const heightRatio = adjustedCropHeight / v1.imageHeight;

  return {
    xRatio: adjustedCropX / v1.imageWidth,
    yRatio: adjustedCropY / v1.imageHeight,
    widthRatio,
    heightRatio,
    circularCrop: v1.circularCrop,
    baseImage: { width: v1.imageWidth, height: v1.imageHeight },
  };
}

export const imageSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.IMAGE),
  imageUrl: z.string(),
  croppedImageUrl: z.string().optional(),
  padding: paddingSchema,
  horizontalPadding: createEnum(Size, { enumName: "Size" }),
  verticalPadding: createEnum(Size, { enumName: "Size" }),
  showCaption: z.boolean(),
  caption: z.string().optional(),
  altText: z.string().optional(),
  clickthroughUrl: z.string().optional(),
  clickthroughLinkType: createEnum(ButtonLinkType, {
    enumName: "ButtonLinkType",
  }).optional(),
  clickthroughSchemaFieldName: z.string().optional(),
  aspectRatio: z.number().optional(),
  cropConfig: cropConfigV1Schema.optional(),
  cropConfigV2: cropConfigV2Schema.optional(),
  imageSourceType: createEnum(ImageType, { enumName: "ImageType" }).optional(),
});

const trackingInfoSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.TRACKING_INFO),
  textColor: z.string(),
  buttonVisible: z.boolean(),
  buttonColor: z.string(),
  buttonTextColor: z.string(),
  buttonText: z.string().optional(),
  buttonCornerRadius: z.number(),
  buttonPadding: paddingSchema,
  statusIndicatorColor: z.string(),
  statusIndicatorInnerColor: z.string(),
  hideDeliveryEstimate: z.boolean(),
  fontFamily: z.string(),
  schemaFieldName: z.string().optional(),
});

const brandKitEmailButtonSchema = z.object({
  hierarchy: createEnum(buttonHierarchies, { enumName: "ButtonHierarchy" }),
  override: z.object({
    enabled: z.boolean(),
    backgroundColor: z.string().nullish(),
    textColor: z.string(),
    border: z.object({
      cornerRadiusPx: z.number(),
      stroke: z.object({ color: z.string(), weightPx: z.number() }),
    }),
    paddingPx: z.number(),

    font: z.object({
      fontFamily: z.string(),
      fontSizePx: z.number(),
      fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
    }),
  }),
  fullWidth: z.boolean(),
  text: z.string(),
});
export type BrandKitEmailButton = z.infer<typeof brandKitEmailButtonSchema>;

const cornerRadiusSchema = z.object({
  topLeftPx: z.number(),
  topRightPx: z.number(),
  bottomLeftPx: z.number(),
  bottomRightPx: z.number(),
});
export type CornerRadius = z.infer<typeof cornerRadiusSchema>;

/**
 * V2 of BrandKitEmailButton with enhanced padding and corner radius support.
 * Changes from V1:
 * - override.paddingPx (number) → override.padding (Padding with top/right/bottom/left)
 * - override.border.cornerRadiusPx (number) → override.border.cornerRadius (CornerRadius with per-corner values)
 */
const brandKitEmailButtonV2Schema = brandKitEmailButtonSchema
  .omit({ override: true })
  .extend({
    override: brandKitEmailButtonSchema.shape.override
      .omit({ paddingPx: true, border: true })
      .extend({
        padding: paddingSchema,
        border: z.object({
          cornerRadius: cornerRadiusSchema,
          stroke: z.object({ color: z.string(), weightPx: z.number() }),
        }),
      }),
  });
export type BrandKitEmailButtonV2 = z.infer<typeof brandKitEmailButtonV2Schema>;

const trackableSummarySchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.TRACKABLE_SUMMARY),
  showOrderInformation: z.boolean(),
  showCostSummary: z.boolean(),
  showPaymentInformation: z.boolean(),
  showCustomerInformation: z.boolean(),
  showTnplItemsOnly: z.boolean().nullish(),
  showImage: z.boolean().optional(),
  showTitle: z.boolean().optional(),
  showVariant: z.boolean().optional(),
  showPrice: z.boolean().optional(),
  showQuantity: z.boolean().optional(),
  showCustomFields: z.boolean().optional(),
  showCompareAtPrice: z.boolean().optional(),
  showSavingsSummary: z.boolean().optional(),
  createGiftReceiptButton: brandKitEmailButtonSchema.default(
    defaultCreateGiftReceiptButton,
  ),
  showCreateGiftReceiptButton: z.boolean().optional(),
  paymentDelayed: z.boolean().nullish(),
  textColor: z.string(),
  fontFamily: z.string(),
  imageSize: createEnum(Size, { enumName: "Size" }),
  imageCornerRadius: z.number(),
  imageAspectRatio: z.number().nullish(),
  imageObjectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
  schemaFieldName: z.string().optional(),
  customFields: z.array(z.string()).optional(),
  compareAtPriceLabel: z.string().optional(),
  subtotalLabel: z.string().optional(),
  savingsLabel: z.string().optional(),
});

const manuallySelectedProductSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
});

const productsSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.PRODUCTS),
  textColor: z.string(),
  fontFamily: z.string(),
  titleFontSize: z.number().optional(),
  imageCornerRadius: z.number(),
  checkoutButton: inlineButtonSchema,
  /** Only used on abandonment flows */
  showCheckoutButton: z.boolean().optional(),
  lineItemButtons: inlineButtonSchema,
  numberOfProducts: z.number(),
  imageSize: createEnum(Size, { enumName: "Size" }),
  productSelectionType: createEnum(ProductSelectionType, {
    enumName: "ProductSelectionType",
  }),
  showPrice: z.boolean().optional(),
  showTitle: z.boolean().optional(),
  showImage: z.boolean().optional(),
  showButton: z.boolean().optional(),
  showQuantity: z.boolean().optional(),
  layoutType: createEnum(CartLayoutType, {
    enumName: "CartLayoutType",
  }).optional(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  columns: z.number(),
  stackOnMobile: z.boolean(),
  recommendedProductFilterId: zExt.objectId().optional(),
  manuallySelectedProducts: z.array(manuallySelectedProductSchema),
  imageAspectRatio: z.number().nullish(),
  imageObjectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
  schemaFieldName: z.string().optional(),
  provider: createEnum(Provider, { enumName: "Provider" }).default(
    Provider.SHOPIFY,
  ),
});

/**
 * Settings used for product carousel rendering and sizing calculations.
 * This subset of fields is needed by components that render the carousel UI.
 */
const productCarouselSettingsSchema_ = z.object({
  size: createEnum(ShoppableProductsSize, {
    enumName: "ShoppableProductsSize",
  }),
  show: z.object({
    image: z.boolean(),
    title: z.boolean(),
    price: z.boolean(),
    description: z.boolean(),
    button: z.boolean(),
  }),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  cardCornerRadiusPx: z.number(),
  fontFamily: z.string(),
  textColor: z.string(),
  imageAspectRatio: z.number(),
  imageObjectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
});
export const productCarouselSettingsSchema: z.ZodType<ProductCarouselSettings> =
  productCarouselSettingsSchema_;
export interface ProductCarouselSettings
  extends z.infer<typeof productCarouselSettingsSchema_> {}

/**
 * Shared fields between shoppable products and one-click upsell blocks.
 * Both blocks display product cards with similar styling and selection options.
 */
const productCarouselBaseSchema = productCarouselSettingsSchema_.extend({
  productSelectionType: createEnum(ProductSelectionType, {
    enumName: "ProductSelectionType",
  }),
  manuallySelectedProducts: z.array(manuallySelectedProductSchema),
  productFilterId: zExt.objectId().optional(),
  numberOfDynamicProducts: z.number(),
  schemaFieldName: z.string().optional(),
  provider: createEnum(Provider, { enumName: "Provider" }).default(
    Provider.SHOPIFY,
  ),
});

export const shoppableProductsSchema = baseSectionSchema
  .extend(productCarouselBaseSchema.shape)
  .extend({
    type: z.literal(EmailBlockType.SHOPPABLE_PRODUCTS),
    productButtons: brandKitEmailButtonSchema,
    checkoutButton: brandKitEmailButtonSchema,
  });

export enum FontSource {
  BODY = "body",
  HEADING = "heading",
  CUSTOM = "custom",
}

/**
 * Font settings with source selection (body/heading from brand kit, or custom).
 */
const emailFontSettingsSchema_ = z.object({
  source: createEnum(FontSource, { enumName: "FontSource" }),
  sizePx: z.number(),
  color: z.string(),
  // Used when source is "custom"
  override: z.object({
    family: z.string(),
    weight: createEnum(fontWeights, { enumName: "FontWeight" }),
  }),
  bold: z.boolean(),
  italic: z.boolean(),
  underline: z.boolean(),
  strikethrough: z.boolean(),
});
export const emailFontSettingsSchema: z.ZodType<EmailFontSettings> =
  emailFontSettingsSchema_;
export interface EmailFontSettings
  extends z.infer<typeof emailFontSettingsSchema_> {}

export enum OneClickSubscriptionButtonState {
  ACTIVE = "active",
  SUBSCRIBED = "subscribed",
  EXPIRED = "expired",
}

export const oneClickSubscriptionSchema = baseSectionSchema
  .extend(productCarouselSettingsSchema_.shape)
  .extend({
    type: z.literal(EmailBlockType.ONE_CLICK_SUBSCRIPTION),
    schemaFieldName: z.string().nullish(),
    buttons: z.object({
      active: brandKitEmailButtonV2Schema,
      subscribed: brandKitEmailButtonV2Schema,
      expired: brandKitEmailButtonV2Schema,
    }),
    // Text styling for product card content
    text: z.object({
      title: emailFontSettingsSchema,
      originalPrice: emailFontSettingsSchema,
      discountedPrice: emailFontSettingsSchema,
      description: emailFontSettingsSchema,
    }),
    // Frontend-only field to control which button state is shown in the builder preview
    frontendOnlyPreviewButtonState: createEnum(
      OneClickSubscriptionButtonState,
      { enumName: "OneClickSubscriptionButtonState" },
    ).nullish(),
    skipEmailIfEmpty: z.boolean().nullish(),
  });

const scratchToRevealSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SCRATCH_TO_REVEAL),
  discountId: zExt.objectId().optional(),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  cornerRadiusPx: z.number(),
  fontFamily: z.string(),
  fontWeight: createEnum(EmailBuilderFontWeight, {
    enumName: "EmailBuilderFontWeight",
  }),
  fontSizePx: z.number(),
  textColor: z.string(),
  secretBackgroundColor: z.string(),
  scratchOffColor: z.string(),
  scratcherSize: createEnum(ScratcherSize, { enumName: "ScratcherSize" }),
  scratchOffAreaPadding: paddingSchema,
  fallbackText: z.string(),
});

const starColorSchema = z.object({ empty: z.string(), filled: z.string() });

// could be simplified by importing from brand-kit and doing some omit() and extend()
// but holding off since Zod is slow
const brandKitEmailInputSchema = z.object({
  override: z.object({
    enabled: z.boolean(),
    backgroundColor: z.string().optional(),
    textColor: z.string(),
    errorColor: z.string(),
    border: z.object({
      cornerRadiusPx: z.number(),
      stroke: z.object({ color: z.string(), weightPx: z.number() }),
    }),
    paddingPx: z.number(),
    font: z.object({
      fontFamily: z.string(),
      fontSizePx: z.number(),
      fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
    }),
  }),
  fullWidth: z.boolean(),
});

const interactiveReviewRequestSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.INTERACTIVE_REVIEW_REQUEST),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  layout: createEnum(Orientation, { enumName: "Orientation" }),
  star: z.object({
    pointRadius: z.number(),
    color: starColorSchema,
    outline: z.object({
      enabled: z.boolean(),
      widthPx: z.number(),
      color: z.string(),
    }),
  }),
  inputs: brandKitEmailInputSchema,
  placeholderText: z.object({ title: z.string(), description: z.string() }),
  submitButton: brandKitEmailButtonSchema,
  image: z.object({
    aspectRatio: z.number(),
    cornerRadiusPx: z.number(),
    size: createEnum(ShoppableProductsSize, {
      enumName: "ShoppableProductsSize",
    }),
    objectFit: createEnum(ObjectFit, { enumName: "ObjectFit" }).optional(),
  }),
  font: z.object({
    weight: createEnum(fontWeights, { enumName: "FontWeight" }),
    family: z.string(),
    sizePx: z.number(),
  }),
  textColor: z.string(),
  schemaFieldName: z.string().optional(),
  /**
   * Blocks to display when a review is successfully submitted
   */
  successSection: z.object({
    blocks: z.array(
      z.discriminatedUnion("type", [
        textSchema,
        imageSchema,
        discountSchema,
        buttonSchema,
        lineSchema,
        spacerSchema,
      ]),
    ),
    padding: paddingSchema,
    backgroundColor: z.string(),
  }),
});

const subscriptionSkipOrDelaySchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SUBSCRIPTION_SKIP_OR_DELAY),
  delayOptions: z.array(delayOptionSchema),
  chargeDate: z.object({
    enabled: z.boolean(),
    dateFormat: createEnum(SubscriptionDateFormat, {
      enumName: "SubscriptionDateFormat",
    }),
  }),
  skipOption: z.object({ enabled: z.boolean() }),
  delayedPage: z.object({ title: z.string(), subtitle: z.string() }),
  skippedPage: z.object({ title: z.string(), subtitle: z.string() }),
  initialPage: z.object({
    title: z.string(),
    buttons: brandKitEmailButtonSchema, // these settings also apply to buttons not on the initial page, confusingly
  }),
  font: z.object({
    weight: createEnum(fontWeights, { enumName: "FontWeight" }),
    family: z.string(),
    sizePx: z.number(),
  }),
  textColor: z.string(),
});
export type SubscriptionSkipOrDelay = z.infer<
  typeof subscriptionSkipOrDelaySchema
>;

export type InteractiveReviewRequestSuccessBlock =
  | EmailBlock.Text
  | EmailBlock.Image
  | EmailBlock.Discount
  | EmailBlock.Button
  | EmailBlock.Line
  | EmailBlock.Spacer;

const subscriptionAddToOrderSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SUBSCRIPTION_ADD_TO_ORDER),
});

const subscriptionSwapProductSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SUBSCRIPTION_SWAP_PRODUCT),
});

const chromeDinoSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.CHROME_DINO),
});

export const sectionReferenceSchema = baseSectionSchema.extend({
  type: z.literal(EmailBlockType.SECTION_REFERENCE),
  sectionId: zExt.objectId(),
  sectionName: z.string().optional(), // For display purposes
});

const nonRecursiveEmailBlocks = z.discriminatedUnion("type", [
  discountSchema,
  menuSchema,
  socialsSchema,
  headerSchema,
  footerSchema,
  textSchema,
  buttonSchema,
  reviewRequestSchema,
  lineSchema,
  spacerSchema,
  imageSchema,
  trackingInfoSchema,
  trackableSummarySchema,
  productsSchema,
  shoppableProductsSchema,
  oneClickSubscriptionSchema,
  scratchToRevealSchema,
  subscriptionSkipOrDelaySchema,
  subscriptionAddToOrderSchema,
  subscriptionSwapProductSchema,
  chromeDinoSchema,
  sectionReferenceSchema,
  qrCodeSchema,
]);

const emailBlockSchema_ = z.discriminatedUnion("type", [
  ...nonRecursiveEmailBlocks.def.options,
  columnSchema,
  interactiveReviewRequestSchema,
]);

export const emailBlockSchema: z.ZodType<EmailBlock> = emailBlockSchema_;
export interface IEmailBlockSchema extends z.ZodType<EmailBlock> {}

const emailFooterAddress = z.object({
  /** Business name that appears in email footer (e.g. "Acme Corp") */
  businessAddress: z.string().optional().nullable(),
  /** Street address including both lines if needed (e.g. "123 Main Street Suite 200") */
  legalAddress: z.string(),
  /** Combined city, state, and postal code (e.g. "New York, NY 10001") */
  cityStateZip: z.string(),
  /** Country name (e.g. "United States") */
  country: z.string(),
});
export type EmailFooterAddress = z.infer<typeof emailFooterAddress>;

export const emailTemplateSchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  subject: z.string(),
  templateType: createEnum(TemplateType, { enumName: "TemplateType" }),
  category: createEnum(categories, { enumName: "Category" }),
  schemaType: createEnum(SchemaType, { enumName: "SchemaType" }),
  emailPreview: z.string().optional().nullable(),
  emailBackgroundColor: z.string(),
  contentBackgroundColor: z.string(),
  address: emailFooterAddress.nullish(),
  sections: z.array(emailBlockSchema),
  linkColor: z.string().optional().nullable(),
  team: zExt.objectId().optional(),
  createdAt: z.coerce.date().nullish(),
  updatedAt: z.coerce.date().nullish(),
  originTemplateId: zExt.objectId().optional(),
  isPlainText: z.boolean().optional(),
});
export interface EmailTemplate extends z.infer<typeof emailTemplateSchema> {}

/**
 * Templates with these schema types will not show up in the global "email templates" or "sms templates" pages in the merchant dashboard.
 *
 * This array is not expected to live very long since we will be significantly refactoring the way we display templates in the near future.
 */
export const excludedFromListView: SchemaType[] = [
  SchemaType.MARKETING_CAMPAIGN,
];

/**
 * Type-safe mapping of EmailBlockType to schema field names
 * This ensures we have a clear mapping for each block type that requires a schemaFieldName
 */
type SchemaFieldNameMapping = Record<EmailBlockType, string | undefined>;

/**
 * Maps EmailBlockType to the corresponding schema field name
 * Add new mappings here when adding new email block types that require schemaFieldName
 */
const schemaFieldNameMap: SchemaFieldNameMapping = {
  [EmailBlockType.FOOTER]: "unsubscribeLink",
  [EmailBlockType.TRACKABLE_SUMMARY]: "orderSummary",
  [EmailBlockType.REVIEW_REQUEST]: "reviewRequest",
  [EmailBlockType.TRACKING_INFO]: "tracker",
  [EmailBlockType.PRODUCTS]: "cartContext",
  [EmailBlockType.SHOPPABLE_PRODUCTS]: "cartContext",
  [EmailBlockType.INTERACTIVE_REVIEW_REQUEST]: "reviewRequest",
  [EmailBlockType.DISCOUNT]: undefined,
  [EmailBlockType.COLUMN]: undefined,
  [EmailBlockType.LINE]: undefined,
  [EmailBlockType.MENU]: undefined,
  [EmailBlockType.SOCIALS]: undefined,
  [EmailBlockType.HEADER]: undefined,
  [EmailBlockType.TEXT]: undefined,
  [EmailBlockType.BUTTON]: undefined,
  [EmailBlockType.IMAGE]: undefined,
  [EmailBlockType.SPACER]: undefined,
  [EmailBlockType.SCRATCH_TO_REVEAL]: undefined,
  [EmailBlockType.SUBSCRIPTION_SKIP_OR_DELAY]: undefined,
  [EmailBlockType.SUBSCRIPTION_ADD_TO_ORDER]: undefined,
  [EmailBlockType.SUBSCRIPTION_SWAP_PRODUCT]: undefined,
  [EmailBlockType.CHROME_DINO]: undefined,
  [EmailBlockType.SECTION_REFERENCE]: undefined,
  [EmailBlockType.QR_CODE]: undefined,
  [EmailBlockType.ONE_CLICK_SUBSCRIPTION]: "orderSummary",
};

/**
 * Gets the schema field name for a given email block type
 * @param blockType The email block type to get the schema field name for
 * @returns The schema field name if one exists for the block type, undefined otherwise
 */
export function getSchemaFieldNameForBlock(
  blockType: EmailBlockType,
): string | undefined {
  return schemaFieldNameMap[blockType];
}

export namespace EmailBlock {
  export type Header = z.infer<typeof headerSchema>;
  export type Footer = z.infer<typeof footerSchema>;
  export type Text = z.infer<typeof textSchema>;
  export type Button = z.infer<typeof buttonSchema>;
  export type InlineButton = z.infer<typeof inlineButtonSchema>;
  export type Image = z.infer<typeof imageSchema>;
  export type Discount = z.infer<typeof discountSchema>;
  export type Column = z.infer<typeof columnSchema>;
  export type Menu = z.infer<typeof menuSchema>;
  export type QrCode = z.infer<typeof qrCodeSchema>;
  export type Socials = z.infer<typeof socialsSchema>;
  export type ReviewRequest = z.infer<typeof reviewRequestSchema>;
  export type InteractiveReviewRequest = z.infer<
    typeof interactiveReviewRequestSchema
  > & {
    /**
     * This field is intentionally stripped out whenever Zod parses it or saved in the DB.
     * It serves as a way to force the email builder to show the "success" page even before submission.
     * The more general solution would be refactoring the email builder to deal with Builder<Section> instead,
     * but this is a quick hack because YAGNI.
     */
    frontendOnlyForceSuccessState?: boolean;
  };
  export type Line = z.infer<typeof lineSchema>;
  export type Spacer = z.infer<typeof spacerSchema>;
  export type TrackingInfo = z.infer<typeof trackingInfoSchema>;
  export type TrackableSummary = z.infer<typeof trackableSummarySchema>;
  export type Products = z.infer<typeof productsSchema>;
  export type ShoppableProducts = z.infer<typeof shoppableProductsSchema>;
  export type OneClickSubscription = z.infer<typeof oneClickSubscriptionSchema>;
  export type ScratchToReveal = z.infer<typeof scratchToRevealSchema>;
  export type SubscriptionSkipOrDelay = z.infer<
    typeof subscriptionSkipOrDelaySchema
  > & { frontendOnlyForceState?: SubscriptionSkipOrDelayState };
  export type SubscriptionAddToOrder = z.infer<
    typeof subscriptionAddToOrderSchema
  >;
  export type SubscriptionSwapProduct = z.infer<
    typeof subscriptionSwapProductSchema
  >;
  export type ChromeDino = z.infer<typeof chromeDinoSchema>;
  export type SectionReference = z.infer<typeof sectionReferenceSchema>;
}

export type NonRecursiveEmailBlock =
  | EmailBlock.Header
  | EmailBlock.Footer
  | EmailBlock.Text
  | EmailBlock.Button
  | EmailBlock.Line
  | EmailBlock.Spacer
  | EmailBlock.Image
  | EmailBlock.TrackingInfo
  | EmailBlock.TrackableSummary
  | EmailBlock.Products
  | EmailBlock.ReviewRequest
  | EmailBlock.QrCode
  | EmailBlock.Menu
  | EmailBlock.Socials
  | EmailBlock.Discount
  | EmailBlock.ShoppableProducts
  | EmailBlock.OneClickSubscription
  | EmailBlock.ScratchToReveal
  | EmailBlock.SubscriptionSkipOrDelay
  | EmailBlock.SubscriptionAddToOrder
  | EmailBlock.SubscriptionSwapProduct
  | EmailBlock.ChromeDino
  | EmailBlock.SectionReference;

export type RecursiveEmailBlock =
  | EmailBlock.Column
  | EmailBlock.InteractiveReviewRequest;

export type EmailBlock = NonRecursiveEmailBlock | RecursiveEmailBlock;
